import { useCallback, useEffect, useState } from 'react'

export type ThemeMode = 'system' | 'dark' | 'light'
export type ResolvedTheme = 'dark' | 'light'

export const THEME_STORAGE_KEY = 'hermes.theme'

/**
 * Dark is the default, not the only option.
 *
 * A SOC wall is dim and the screen is stared at for a whole shift, so dark wins
 * on first run. But plenty of platform engineers work in a lit office, and
 * "system" is offered precisely so the choice is not forced.
 */
export const DEFAULT_MODE: ThemeMode = 'dark'

const MODES: readonly ThemeMode[] = ['system', 'dark', 'light']

export function readStoredMode(): ThemeMode {
  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY)
    return MODES.find((mode) => mode === raw) ?? DEFAULT_MODE
  } catch {
    // Private mode, blocked storage — the default is still correct.
    return DEFAULT_MODE
  }
}

export function systemTheme(): ResolvedTheme {
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark'
}

export function resolveTheme(mode: ThemeMode): ResolvedTheme {
  return mode === 'system' ? systemTheme() : mode
}

function apply(theme: ResolvedTheme): void {
  document.documentElement.dataset['theme'] = theme
}

export function useTheme(): {
  mode: ThemeMode
  resolved: ResolvedTheme
  setMode: (mode: ThemeMode) => void
} {
  const [mode, setModeState] = useState<ThemeMode>(readStoredMode)
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolveTheme(readStoredMode()))

  useEffect(() => {
    const next = resolveTheme(mode)
    setResolved(next)
    apply(next)

    if (mode !== 'system') return

    const query = window.matchMedia('(prefers-color-scheme: light)')
    const onChange = (): void => {
      const updated = systemTheme()
      setResolved(updated)
      apply(updated)
    }
    query.addEventListener('change', onChange)
    return () => query.removeEventListener('change', onChange)
  }, [mode])

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next)
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next)
    } catch {
      // Preference is lost on reload; the session still honours it.
    }
  }, [])

  return { mode, resolved, setMode }
}
